import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { fetchAutoPayStatus } from 'redux/autoPayStatus/autoPayStatusSlice'
import accountSummaryAPI from 'services/accountSummary'
import {
  AccountSummaryResponse,
  AnnualPercentageRateType,
  Currency,
  GetAccountSummaryRequest,
  LineOfBusiness,
  ProductTypes
} from 'services/accountSummary/typings'
import { StatementEnrollment } from 'typings/statement'
import { ServerError } from 'typings/serverError'

export interface AccountSummaryState {
  isLoading: boolean
  response: AccountSummaryResponse
  error: ServerError
}

export const initState: AccountSummaryState = {
  isLoading: false,
  response: {
    accountInfo: {
      accountLast4: ''
    },
    profile: {
      'cipher.firstName': ''
    },
    product: {
      portfolio: '',
      type: ProductTypes.dualCard,
      lineOfBusiness: LineOfBusiness.retailCard,
      displayName: '',
      clientId: '',
      portfolioLogo: ''
    },
    card: {
      cardArtURL: ''
    },
    billing: {
      statementEnrollment: StatementEnrollment.electronic,
      autopayEnrollment: false,
      lastPaymentAmount: '',
      lastPaymentDate: '',
      statementBalance: '',
      nextPaymentDate: '',
      pastDue: false,
      pastDueAmount: '',
      daysDelinquent: '',
      customerAssistancePlan: false,
      settlementEnrolled: false,
      fixedPaymentAmount: '',
      interestPaid: '',
      totalMinimumPaymentDue: ''
    },
    credit: {
      annualPercentageRate: '',
      annualPercentageRateType: AnnualPercentageRateType.fixed,
      availableCredit: '',
      creditLimit: '',
      currentBalance: '',
      temporaryCreditLimit: ''
    },
    installment: {
      principalAmount: '',
      monthlyAmountDue: '',
      totalAmountPaid: '',
      startDate: '',
      endDate: '',
      interestRate: '',
      loanTerm: '',
      payoffValidThrough: '',
      purchaseAmount: '',
      principalRemaining: '',
      termRemaining: 1
    },
    rewards: {
      currency: Currency.dollars,
      rewardsBalance: ''
    },
    servicing: {
      allowable: false,
      reason: ''
    },
    conditions: []
  },
  error: null
}

export const fetchAccountSummary = createAsyncThunk(
  'accountSummarySlice/fetchAccountSummary',
  async (payload: GetAccountSummaryRequest, { dispatch }) => {
    const response = await accountSummaryAPI.getAccountSummary(payload)
    const { autopayEnrollment } = response.billing

    if (autopayEnrollment) {
      await dispatch(fetchAutoPayStatus(payload))
    }

    return response
  }
)

export const accountSummarySlice = createSlice({
  name: 'accountSummarySlice',
  initialState: initState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAccountSummary.pending, (state: AccountSummaryState) => {
        state.isLoading = true
      })
      .addCase(
        fetchAccountSummary.fulfilled,
        (
          state: AccountSummaryState,
          action: PayloadAction<AccountSummaryResponse>
        ) => {
          state.isLoading = false
          state.response = action.payload
        }
      )
      .addCase(
        fetchAccountSummary.rejected,
        (state: AccountSummaryState, action: PayloadAction<unknown>) => {
          state.isLoading = false
          state.error = action.payload as ServerError
        }
      )
  }
})

export default accountSummarySlice.reducer
